import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import NewCase from './pages/NewCase';
import EditCase from './pages/EditCase';
import SendFiles from './pages/SendFiles';
import Inbox from './pages/Inbox';
import EngineMonitor from './pages/EngineMonitor';
import CaseViewer from './pages/CaseViewer';
import TermsGate from './pages/TermsGate';
import useInactivityTimer from './hooks/useInactivityTimer';
import './App.css';

function App() {
  const [user, setUser]                 = useState(null);
  const [loading, setLoading]           = useState(true);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isUploading, setIsUploading]   = useState(false);
  const [showWarning, setShowWarning]   = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      // Ignore anonymous sessions (used by the public /send page) for app auth
      if (u && u.isAnonymous) { setUser(null); setLoading(false); return; }
      setUser(u);
      if (u) {
        try {
          const snap = await getDoc(doc(db, 'users', u.uid));
          setTermsAccepted(snap.exists() && snap.data().termsAccepted === true);
        } catch {
          setTermsAccepted(false);
        }
      } else {
        setTermsAccepted(false);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const handleWarn   = useCallback(() => { if (user) setShowWarning(true); }, [user]);
  const handleLogout = useCallback(() => {
    setShowWarning(false);
    if (user) signOut(auth);
  }, [user]);

  const handleStayLoggedIn = () => {
    setShowWarning(false);
  };

  useInactivityTimer({
    onWarn:   handleWarn,
    onLogout: handleLogout,
    paused:   isUploading || !user,
  });

  if (loading) return (
    <div className="loading-screen">
      <div className="loading-logo">ARCHX<span className="gold">AI</span></div>
    </div>
  );

  // Public file intake page — no login, render before the terms gate
  if (window.location.pathname === '/send') {
    return (
      <Router>
        <Routes>
          <Route path="/send" element={<SendFiles />} />
          <Route path="*" element={<Navigate to="/send" />} />
        </Routes>
      </Router>
    );
  }

  // Show Terms Gate for logged-in users who haven't accepted yet
  if (user && !termsAccepted) {
    return <TermsGate user={user} onAccepted={() => setTermsAccepted(true)} />;
  }

  return (
    <>
      {showWarning && (
        <div style={{
          position:'fixed', inset:0, background:'rgba(0,0,0,0.6)',
          zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center'
        }}>
          <div style={{
            background:'white', borderRadius:'16px', padding:'36px 40px',
            maxWidth:'400px', width:'90%', textAlign:'center',
            boxShadow:'0 24px 60px rgba(0,0,0,0.25)'
          }}>
            <div style={{fontSize:'36px', marginBottom:'12px'}}>⏱</div>
            <div style={{fontSize:'20px', fontWeight:'700', color:'#111', marginBottom:'8px'}}>
              Still there?
            </div>
            <div style={{fontSize:'14px', color:'#666', marginBottom:'24px', lineHeight:1.6}}>
              You've been inactive for 25 minutes. For security, you'll be signed out in <strong>5 minutes</strong> unless you continue.
            </div>
            <div style={{display:'flex', gap:'12px', justifyContent:'center'}}>
              <button
                onClick={handleLogout}
                style={{
                  padding:'10px 20px', borderRadius:'8px',
                  border:'1px solid #e5e7eb', background:'white',
                  color:'#666', fontWeight:'600', fontSize:'14px', cursor:'pointer'
                }}
              >
                Sign out now
              </button>
              <button
                onClick={handleStayLoggedIn}
                style={{
                  padding:'10px 24px', borderRadius:'8px',
                  border:'none', background:'#c8922a',
                  color:'white', fontWeight:'700', fontSize:'14px', cursor:'pointer'
                }}
              >
                Stay logged in
              </button>
            </div>
          </div>
        </div>
      )}

      <Router>
        <Routes>
          <Route path="/login"     element={!user ? <Login /> : <Navigate to="/dashboard" />} />
          <Route path="/dashboard" element={user  ? <Dashboard user={user} /> : <Navigate to="/login" />} />
          <Route path="/cases/new" element={user  ? <NewCase user={user} setIsUploading={setIsUploading} /> : <Navigate to="/login" />} />
          <Route path="/cases/:id/edit" element={user ? <EditCase user={user} setIsUploading={setIsUploading} /> : <Navigate to="/login" />} />
          <Route path="/cases/:id/viewer" element={user ? <CaseViewer user={user} /> : <Navigate to="/login" />} />
          <Route path="/inbox"     element={user  ? <Inbox user={user} /> : <Navigate to="/login" />} />
          <Route path="/engine"    element={user  ? <EngineMonitor user={user} /> : <Navigate to="/login" />} />
          <Route path="/send"      element={<SendFiles />} />
          <Route path="*"          element={<Navigate to={user ? "/dashboard" : "/login"} />} />
        </Routes>
      </Router>
    </>
  );
}

export default App;
